import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import { useCaptcha } from "../../../shared/hooks/useCaptcha";
import { useFormRequest } from "../../../shared/hooks/useFormRequest";
import { getSafeRedirect } from "../../../shared/utils/redirect";

import { login, register, requestOtp } from "../api/authApi";
import { AUTH_MESSAGES } from "../messages";
import type { AuthMode, AuthStep } from "../types";

// All state and logic of the login/register flow, so components only render
export const useAuthForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { lang } = useLanguage();
    const { refreshUser } = useAuth();

    const { loading, error, setError, submit } = useFormRequest(AUTH_MESSAGES.generic);
    const captcha = useCaptcha();

    const [mode, setMode] = useState<AuthMode>("login");
    const [step, setStep] = useState<AuthStep>("details");

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");

    // Back to a clean first step (the captcha token was spent)
    const restart = () => {
        setStep("details");
        setOtp("");
        setError("");
        captcha.reset();
    };

    // Step 1: validate, then ask the server to send the SMS code
    const submitDetails = async () => {
        if (mode === "register" && !name.trim()) return setError(AUTH_MESSAGES.nameRequired);
        if (!phone) return setError(AUTH_MESSAGES.phoneRequired);
        if (!captcha.token) return setError(AUTH_MESSAGES.captchaRequired);

        const sent = await submit(
            () => requestOtp({ phone_number: phone, captchaToken: captcha.token }),
            AUTH_MESSAGES.otpFailed
        );

        if (sent) setStep("otp");
        else captcha.reset(); // The token was spent on the failed attempt
    };

    // Step 2: verify the code, refresh the session, then leave the page
    const submitOtp = async () => {
        if (!otp) return setError(AUTH_MESSAGES.otpRequired);

        const authenticated = await submit(
            () =>
                mode === "login"
                    ? login({ phone_number: phone, otp })
                    : register({ phone_number: phone, name, otp }),
            AUTH_MESSAGES.authFailed
        );

        if (!authenticated) return;

        // Update AuthProvider so the app knows the user is logged in without a page reload
        await refreshUser();

        const target = getSafeRedirect(searchParams.get("redirect"), ROUTES.myCourses(lang));
        navigate(target, { replace: true });
    };

    const switchMode = () => {
        setMode((current) => (current === "login" ? "register" : "login"));
        restart();
    };

    return {
        mode,
        step,
        name,
        phone,
        otp,
        error,
        loading,
        captchaKey: captcha.resetKey,
        setName,
        setPhone,
        setOtp,
        setCaptchaToken: captcha.setToken,
        submitDetails,
        submitOtp,
        backToDetails: restart,
        switchMode
    };
};